import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  answerComment,
  commentKey,
  type ChecklistAnswers,
  type ChecklistQuestion,
  type ChecklistSchema,
} from '@/api/checklists.api';

type Props = {
  schema: ChecklistSchema;
  answers: ChecklistAnswers;
  onChange: (answers: ChecklistAnswers) => void;
  readOnly?: boolean;
  /** Highlight unanswered required questions (set after a failed submit). */
  showErrors?: boolean;
};

const isBlank = (v: unknown) =>
  v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

/**
 * Renders a checklist schema as a fillable (or read-only) form — the "Form"
 * half of the review toggle, and the surface used when filling a checklist
 * from an Algebra register row.
 */
export function ChecklistFormView({ schema, answers, onChange, readOnly, showErrors }: Props) {
  const { t } = useTranslation();

  const setAnswer = (questionId: string, value: ChecklistAnswers[string]) => {
    if (readOnly) return;
    onChange({ ...answers, [questionId]: value });
  };

  const setComment = (questionId: string, text: string) => {
    if (readOnly) return;
    onChange({ ...answers, [commentKey(questionId)]: text || null });
  };

  let n = 0;

  return (
    <div className="space-y-5">
      {schema.sections.map((section) => (
        <section key={section.id}>
          <div className="border-l-4 border-primary bg-primary-50/40 px-4 py-2.5 rounded-r-md">
            <h4 className="text-[15px] font-semibold text-gray-900">{section.title}</h4>
            {section.description && (
              <p className="text-[12px] text-muted mt-0.5">{section.description}</p>
            )}
          </div>

          <div className="mt-3 space-y-4 px-1">
            {section.questions.map((q) => {
              n += 1;
              const value = answers[q.id];
              const missing = showErrors && q.required && isBlank(value);
              return (
                <div
                  key={q.id}
                  className={`rounded-md border px-4 py-3 ${
                    missing ? 'border-red-300 bg-danger-bg/40' : 'border-border bg-white'
                  }`}
                >
                  <div className="text-[13px] font-medium text-gray-900">
                    {n}. {q.label}
                    {q.required && <span className="text-danger ml-0.5">*</span>}
                  </div>
                  {q.help && <p className="text-[12px] text-muted mt-0.5">{q.help}</p>}
                  <div className="mt-2">
                    <AnswerInput
                      question={q}
                      value={value}
                      readOnly={readOnly}
                      onChange={(v) => setAnswer(q.id, v)}
                    />
                  </div>
                  {q.allowComment && (
                    <CommentBox
                      question={q}
                      value={answerComment(q.id, answers)}
                      answer={value}
                      readOnly={readOnly}
                      showErrors={showErrors}
                      onChange={(text) => setComment(q.id, text)}
                    />
                  )}
                  {missing && (
                    <p className="text-[12px] text-danger mt-1.5">{t('checklist.required_hint')}</p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

/**
 * The "why did you answer that?" box. Collapsed behind a button so a form of
 * Yes/No questions doesn't turn into a wall of textareas — but it opens itself
 * whenever a comment already exists or the chosen answer demands one.
 */
function CommentBox({
  question,
  value,
  answer,
  readOnly,
  showErrors,
  onChange,
}: {
  question: ChecklistQuestion;
  value: string;
  answer: ChecklistAnswers[string];
  readOnly?: boolean;
  showErrors?: boolean;
  onChange: (text: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const requiredFor = question.commentRequiredFor ?? [];
  const isRequired = answer != null && requiredFor.includes(String(answer));
  const missing = isRequired && !value.trim();

  if (readOnly) {
    if (!value) return null;
    return (
      <div className="mt-2 rounded-md border border-border bg-[#FAFBFC] px-3 py-2">
        <div className="text-[11px] font-semibold uppercase tracking-[0.04em] text-muted">
          {t('checklist.comment')}
        </div>
        <p className="mt-0.5 whitespace-pre-wrap text-[13px] text-gray-900">{value}</p>
      </div>
    );
  }

  if (!open && !value && !isRequired) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 text-[12px] font-medium text-primary hover:underline"
      >
        ＋ {t('checklist.add_comment')}
      </button>
    );
  }

  return (
    <div className="mt-2">
      <div className="flex items-baseline gap-2">
        <span className="text-[12px] font-medium text-gray-900">
          {t('checklist.comment')}
          {isRequired ? (
            <span className="text-danger ml-0.5">*</span>
          ) : (
            <span className="text-muted font-normal"> · {t('checklist.optional')}</span>
          )}
        </span>
        {isRequired && (
          <span className="text-[11px] text-muted">
            {t('checklist.comment_required_because', { answer: String(answer) })}
          </span>
        )}
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        className={`textarea mt-1 ${showErrors && missing ? 'border-red-300' : ''}`}
        placeholder={t('checklist.comment_placeholder') as string}
      />
      {showErrors && missing && (
        <p className="text-[12px] text-danger mt-1">{t('checklist.comment_required_hint')}</p>
      )}
    </div>
  );
}

function AnswerInput({
  question,
  value,
  readOnly,
  onChange,
}: {
  question: ChecklistQuestion;
  value: ChecklistAnswers[string];
  readOnly?: boolean;
  onChange: (value: ChecklistAnswers[string]) => void;
}) {
  const { t } = useTranslation();
  const options = question.options ?? [];

  switch (question.type) {
    case 'long_text':
      return (
        <textarea
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={readOnly}
          rows={3}
          className="textarea"
          placeholder={t('checklist.answer_placeholder') as string}
        />
      );

    case 'number':
      return (
        <input
          type="number"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          disabled={readOnly}
          className="input sm:max-w-[220px]"
        />
      );

    case 'date':
      return (
        <input
          type="date"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={readOnly}
          className="input sm:max-w-[220px]"
        />
      );

    case 'dropdown':
      return (
        <select
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={readOnly}
          className="select sm:max-w-[320px]"
        >
          <option value="">{t('checklist.choose')}</option>
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );

    case 'multi_choice': {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="flex flex-col gap-1.5">
          {options.map((o) => (
            <label key={o} className="flex items-center gap-2 text-[13px] text-gray-900">
              <input
                type="checkbox"
                checked={selected.includes(o)}
                disabled={readOnly}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...selected, o]
                    : selected.filter((s) => s !== o);
                  onChange(next.length ? next : null);
                }}
                className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary/20"
              />
              {o}
            </label>
          ))}
        </div>
      );
    }

    case 'rating': {
      const max = question.max ?? 5;
      const current = typeof value === 'number' ? value : 0;
      return (
        <div className="flex flex-wrap items-center gap-1.5">
          {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              disabled={readOnly}
              onClick={() => onChange(current === n ? null : n)}
              className={`h-8 w-8 rounded-md border text-[13px] font-medium transition-colors ${
                current === n
                  ? 'border-primary bg-primary text-white'
                  : 'border-border-strong bg-white text-gray-900 hover:border-primary'
              } disabled:opacity-60`}
            >
              {n}
            </button>
          ))}
        </div>
      );
    }

    case 'single_choice':
    case 'yes_no':
    case 'yes_no_na':
      return (
        <div className="flex flex-wrap gap-x-5 gap-y-1.5">
          {options.map((o) => (
            <label key={o} className="flex items-center gap-2 text-[13px] text-gray-900">
              <input
                type="radio"
                name={question.id}
                checked={value === o}
                disabled={readOnly}
                onChange={() => onChange(o)}
                className="h-4 w-4 border-border-strong text-primary focus:ring-primary/20"
              />
              {o}
            </label>
          ))}
        </div>
      );

    default:
      return (
        <input
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={readOnly}
          className="input"
          placeholder={t('checklist.answer_placeholder') as string}
        />
      );
  }
}
